import PropTypes from 'prop-types'
import R from 'ramda'
import { createPortal } from 'react-dom'
import {
  DndContext,
  DragOverlay,
  closestCenter,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core'
import {
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
} from '@dnd-kit/sortable'
import { useEffect, useRef, useState } from 'react'

import FlashcardsDigital from 'Images/flashcards_digital.svg'
import {
  Box,
  Button,
  Columns,
  Col,
  Icon,
  Heading,
  Image,
  Text,
  TransparentButton,
} from '@redriverpress/system'

import UserFlashcard from '../UserFlashcard'
import UserFlashcardDragOverlay from '../UserFlashcardDragOverlay'
import { flashcardsPropType } from '../propTypes'
import {
  useFlashcardSelection,
  useFlashcardSelectionSize,
} from '../hooks/useFlashcardSelection'

export default function ManageFlashcards({
  deleteAllAction,
  flashcards,
  onRemove,
  onRename,
  onReorder,
  onCopy,
}) {
  const [mode, setMode] = useState('default')

  const [activeId, setActiveId] = useState(null)
  const activeFlashcard = R.find(R.propEq('id', activeId), flashcards)
  const {
    selection,
    checkSelection,
    refreshSelectionWith,
    toggleSelection,
  } = useFlashcardSelection()

  const sensors = useSensors(
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
    useSensor(MouseSensor, {
      activationConstraint: {
        distance: 10,
      },
    }),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 250,
        tolerance: 5,
      },
    })
  )

  const onDragStart = dragEvent => {
    setActiveId(dragEvent.active.id)
  }

  const onDragCancel = () => {
    setActiveId(null)
  }

  const onDragEnd = dragEvent => {
    const { active, over } = dragEvent

    if (active.id !== over.id) {
      const oldIndex = R.findIndex(R.propEq('id', active.id), flashcards)
      const newIndex = R.findIndex(R.propEq('id', over.id), flashcards)

      onReorder({ flashcards, oldIndex, newIndex, flashcardId: activeId })
    }

    setActiveId(null)
  }

  const doneReorderingRef = useRef()

  useEffect(() => {
    // Keyboard accessibility assistance
    mode === 'reorder' && doneReorderingRef.current.focus()
  }, [mode])

  const selectionSize = useFlashcardSelectionSize(selection)

  useEffect(() => {
    // Reset selection when flashcards change. For example, remove from selection deleted flashcards.
    refreshSelectionWith(flashcards)
  }, [flashcards, refreshSelectionWith])

  const handleCopySelection = () => {
    // Must override id with flashcardId. We must send Flashcards to the event, not UserFlashcards
    const userFlashcardToFlashcard = userFlashcard => {
      const { flashcardId, userTitle, ...rest } = userFlashcard

      return {
        ...rest,
        id: flashcardId,
      }
    }

    const result = flashcards
      .filter(flashcard => checkSelection(selection, flashcard.id))
      .map(userFlashcardToFlashcard)

    onCopy(result)
  }

  const copyEnabled = Boolean(onCopy)

  return flashcards.length === 0 ? (
    <Box py={[4, null, 5]} px={3} textAlign='center'>
      <Image src={FlashcardsDigital} width='9rem' height='auto' alt='' />
      <Box my={4}>
        <Heading as='h4' mb={3}>
          This flashcard set is empty!
        </Heading>
        <Text measure mx='auto'>
          Browse our flashcards section and select flashcards to curate a
          custom&nbsp;set.
        </Text>
      </Box>
      <TransparentButton
        as='a'
        href='/flashcards'
        fontWeight={600}
        color='blue700'
      >
        <Icon icon='bolt' pr={2} /> Explore Flashcards
      </TransparentButton>
    </Box>
  ) : (
    <Box px={3} py={4} bg='gray100'>
      {mode === 'default' && (
        <Columns mb={4} alignItems='center'>
          <Col width={[1 / 2, null, 'auto']}>
            <Button
              small
              secondary
              width={[1, null, 'auto']}
              onClick={() => setMode('edit')}
            >
              <Icon icon='pencil' pr={2} />
              Edit
            </Button>
          </Col>
          <Col width={[1 / 2, null, 'auto']}>
            <Button
              small
              secondary
              width={[1, null, 'auto']}
              onClick={() => setMode('reorder')}
            >
              <Icon icon='arrows' pr={2} />
              Reorder
            </Button>
          </Col>
          {deleteAllAction && (
            <Col
              width={[1, null, 'auto']}
              display='inline-flex'
              justifyContent={['center', null, 'end']}
              mt={[4, null, 0]}
              ml={[null, null, 'auto']}
            >
              {deleteAllAction}
            </Col>
          )}
        </Columns>
      )}

      {mode === 'edit' && (
        <Box mb={4}>
          <Columns mb={4} alignItems='center'>
            <Col width={[1, 'auto']}>
              <Button small width={1} onClick={() => setMode('default')}>
                <Icon icon='check' pr={2} />
                Done Editing
              </Button>
            </Col>

            {copyEnabled && (
              <Col width={[1, 'auto']} mt={[3, 0]}>
                <Button
                  secondary
                  small
                  width={1}
                  pl={[0, null, 3]}
                  disabled={selectionSize === 0}
                  onClick={handleCopySelection}
                >
                  <Icon icon='copy' pr={2} />
                  Copy Selection
                </Button>
              </Col>
            )}
          </Columns>
        </Box>
      )}

      {mode === 'reorder' && (
        <Button
          small
          width={[1, null, 'auto']}
          mb={4}
          onClick={() => setMode('default')}
          ref={doneReorderingRef}
        >
          <Icon icon='check' pr={2} />
          Done Reordering
        </Button>
      )}

      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={onDragStart}
        onDragCancel={onDragCancel}
        onDragEnd={onDragEnd}
      >
        <SortableContext items={flashcards} strategy={rectSortingStrategy}>
          <Box
            display='grid'
            gridAutoRows='1fr'
            gridTemplateColumns={[
              '1fr',
              null,
              'repeat(4, 1fr)',
              'repeat(6, 1fr)',
            ]}
            gridGap='1rem'
            style={{
              WebkitUserSelect: mode === 'reorder' ? 'none' : 'auto',
            }}
          >
            {flashcards.map(fc => (
              <UserFlashcard
                key={fc.id}
                id={fc.id}
                thumbnail={fc.thumbnail}
                title={fc.title}
                userTitle={fc.userTitle}
                onRemove={onRemove}
                onRename={onRename}
                mode={mode}
                selectable={copyEnabled}
                selected={checkSelection(selection, fc.id)}
                onSelectionChange={toggleSelection}
              />
            ))}
          </Box>
        </SortableContext>
        {/*
         * DragOverlay needs to be portalled in order to sync with cursor when it
         * moves to the bounds of the scrolling parent (in this case, the Dialog).
         */}
        {createPortal(
          <DragOverlay>
            {activeId ? (
              <UserFlashcardDragOverlay
                userTitle={activeFlashcard.userTitle}
                thumbnail={activeFlashcard.thumbnail}
              />
            ) : null}
          </DragOverlay>,
          document.body
        )}
      </DndContext>
    </Box>
  )
}

ManageFlashcards.defaultProps = {
  deleteAllAction: null,
}

ManageFlashcards.propTypes = {
  deleteAllAction: PropTypes.node,
  flashcards: flashcardsPropType.isRequired,
  onRemove: PropTypes.func.isRequired,
  onRename: PropTypes.func.isRequired,
  onReorder: PropTypes.func.isRequired,
  onCopy: PropTypes.func.isRequired,
}
